import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selector as fspConfigPayALoanSelector } from 'selectors/fspConfigPayALoan';
import normalizeAssertionBaseUrl from 'util/normalizeAssertionBaseUrl';
import guestLoanUrlToEnrolledPalIfpUrl from 'util/guestLoanUrlToEnrolledPalIfpUrl';
import { buildPalIfpV2Url } from 'util/ifpGuestUrl';
import { mintEnrolledPayerPalJwt } from 'util/api';
import { getAuth } from 'util/index';
import { unmask as unmaskInternalAccount } from 'sdk/internalAccount';
import {
  buildEnrolledPalContext,
  splitLoanAccountNumber,
} from 'components/modules/service/PayerDetail/TransactAsButton/palContext';

export const resolveEnrolledPalContext = async (transactPayload, fspId) => {
  const ctx = buildEnrolledPalContext(transactPayload);
  if (ctx.loanNumber || !transactPayload.loanId || !fspId) {
    return ctx;
  }

  const unmaskedLoanNumber = await unmaskInternalAccount({
    id: transactPayload.loanId,
    fspId,
  });
  const parsed = splitLoanAccountNumber(unmaskedLoanNumber);

  if (!parsed.loanNumber) {
    throw new Error('Unable to resolve loan information for PAL payment.');
  }

  return {
    ...ctx,
    loanNumber: parsed.loanNumber,
    accountSuffix: ctx.accountSuffix || parsed.accountSuffix,
  };
};

const IDLE = { status: 'idle', error: null, jwt: null, assertionBaseUrl: null };

const useEnrolledPalLaunch = ({
  fspId,
  payerId,
  userId,
  loanContext,
  preOpenedWindow,
  readOnly,
}) => {
  const { id: adminUserId } = useSelector(getAuth);
  const fspConfigPayALoan = useSelector((state) =>
    fspConfigPayALoanSelector(state, fspId)
  );
  const [launch, setLaunch] = useState(IDLE);
  const startedRef = useRef(false);

  const assertionBaseUrl = useMemo(() => {
    const raw = fspConfigPayALoan?.guestUrl;
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) {
      return normalizeAssertionBaseUrl(guestLoanUrlToEnrolledPalIfpUrl(raw));
    }
    return normalizeAssertionBaseUrl(buildPalIfpV2Url(raw));
  }, [fspConfigPayALoan]);

  let guardError = null;
  if (!payerId || !userId) {
    guardError = 'Missing payer registration data for PAL payment.';
  } else if (!assertionBaseUrl) {
    guardError = 'PAL IFP URL is not configured for this FSP.';
  }

  useEffect(() => {
    if (
      startedRef.current ||
      guardError ||
      !preOpenedWindow ||
      preOpenedWindow.closed
    ) {
      return;
    }

    startedRef.current = true;
    setLaunch({ ...IDLE, status: 'minting' });

    const run = async () => {
      try {
        const enrolledPalContext = await resolveEnrolledPalContext(
          loanContext || {},
          fspId
        );
        const result = await mintEnrolledPayerPalJwt(fspId, {
          payerId,
          userId,
          assertionBaseUrl,
          adminUserId,
          ...enrolledPalContext,
          ...(readOnly === true ? { readOnly } : {}),
        });
        setLaunch({
          status: 'success',
          error: null,
          jwt: result.jwt,
          assertionBaseUrl: result.assertionBaseUrl || assertionBaseUrl,
        });
      } catch (err) {
        setLaunch({
          ...IDLE,
          status: 'error',
          error:
            (err && err.data && err.data.message) ||
            (err && err.message) ||
            'Unable to start PAL payment.',
        });
      }
    };

    run();
  }, [
    adminUserId,
    assertionBaseUrl,
    fspId,
    guardError,
    loanContext,
    payerId,
    preOpenedWindow,
    readOnly,
    userId,
  ]);

  if (launch.status === 'idle' && guardError) {
    return { ...launch, status: 'error', error: guardError, assertionBaseUrl };
  }
  return {
    ...launch,
    assertionBaseUrl: launch.assertionBaseUrl || assertionBaseUrl,
  };
};

export default useEnrolledPalLaunch;
